import { useState } from "react";
import { axiosInstance } from "../../../utils/axios";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";

const TONER_COLORS = ["Black", "Cyan", "Magenta", "Yellow"];

export default function TonerModelForm({ updateData }) {
  const emptyForm = {
    part_number: "",
    color: "",
    machine_model: "",
    toner_model: "",
    price: "",
    toner_price: "",
    weight: "",
    units: 0,
    device_brand_id: "",
  };
  const [formData, setFormData] = useState(emptyForm);
  const [error, setError] = useState(null);

  const { data: brands = [] } = useQuery({
    queryKey: ["deviceBrands"],
    queryFn: async () => {
      const response = await axiosInstance.get(
        window.REACT_APP_API_ENDPOINT + "/device-brands?type=all"
      );
      return response.data.data || [];
    },
    onError: () => {
      setError("Failed to fetch device brands. Please try again.");
      toast.error("Failed to fetch device brands");
    },
  });

  // Existing toner models for the selected brand (autocomplete suggestions)
  const { data: tonerModelOptions = [] } = useQuery({
    queryKey: ["toner-models", formData.device_brand_id],
    queryFn: async () => {
      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/toners/brand/${formData.device_brand_id}/models`
      );
      return response.data.data;
    },
    enabled: !!formData.device_brand_id,
  });

  const tonerMutation = useMutation({
    mutationFn: async (data) =>
      axiosInstance.post(window.REACT_APP_API_ENDPOINT + "/toners", data),
    onSuccess: () => {
      updateData();
      setError(null);
      setFormData(emptyForm);
      toast.success("Toner created successfully");
    },
    onError: (err) => {
      setError("Failed to save toner. Please try again.");
      toast.error(err?.response?.data?.message || "Failed to save toner");
    },
  });

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    const requiredFields = [
      "part_number",
      "color",
      "machine_model",
      "toner_model",
      "price",
      "weight",
      "device_brand_id",
    ];
    const isAnyFieldEmpty = requiredFields.some(
      (key) => formData[key] === "" || formData[key] === null
    );

    if (isAnyFieldEmpty) {
      setError("Please fill in all required fields.");
      toast.error("Please fill in all required fields");
      return;
    }

    tonerMutation.mutate({
      part_number: formData.part_number,
      color: formData.color,
      machine_model: formData.machine_model,
      toner_model: formData.toner_model,
      price: parseFloat(formData.price),
      toner_price:
        formData.toner_price !== "" && formData.toner_price !== null
          ? parseFloat(formData.toner_price)
          : null,
      weight: parseFloat(formData.weight),
      units: parseInt(formData.units) || 0,
      device_brand_id: formData.device_brand_id,
    });
  };

  return (
    <div className=" mx-auto p-6 bg-white rounded-lg mt-4 shadow-md">
      <form className="space-y-6 max-w-xl ">
        <div>
          <label
            htmlFor="part_number"
            className="block text-sm font-medium text-gray-700"
          >
            Part Number
          </label>
          <input
            id="part_number"
            name="part_number"
            type="text"
            required
            value={formData.part_number}
            onChange={handleFormChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            placeholder="Enter part number"
          />
        </div>

        <div>
          <label
            htmlFor="color"
            className="block text-sm font-medium text-gray-700"
          >
            Color
          </label>
          <select
            id="color"
            name="color"
            required
            value={formData.color}
            onChange={handleFormChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          >
            <option value="">Select Color</option>
            {TONER_COLORS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="machine_model"
            className="block text-sm font-medium text-gray-700"
          >
            Machine Model
          </label>
          <input
            id="machine_model"
            name="machine_model"
            type="text"
            required
            value={formData.machine_model}
            onChange={handleFormChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            placeholder="Enter machine model"
          />
        </div>

        <div>
          <label
            htmlFor="toner_model"
            className="block text-sm font-medium text-gray-700"
          >
            Toner Model
          </label>
          <input
            id="toner_model"
            name="toner_model"
            type="text"
            list="toner-model-options-inline"
            required
            value={formData.toner_model}
            onChange={handleFormChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            placeholder="Enter toner model"
          />
          <datalist id="toner-model-options-inline">
            {tonerModelOptions.map((m) => (
              <option key={m} value={m} />
            ))}
          </datalist>
        </div>

        <div>
          <label
            htmlFor="price"
            className="block text-sm font-medium text-gray-700"
          >
            Price
          </label>
          <input
            id="price"
            name="price"
            type="number"
            step="0.01"
            min="0"
            required
            value={formData.price}
            onChange={handleFormChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            placeholder="Enter price"
          />
        </div>

        <div>
          <label
            htmlFor="toner_price"
            className="block text-sm font-medium text-gray-700"
          >
            Toner Price{" "}
            <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            id="toner_price"
            name="toner_price"
            type="number"
            step="0.01"
            min="0"
            value={formData.toner_price}
            onChange={handleFormChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            placeholder="Enter toner price"
          />
        </div>

        <div>
          <label
            htmlFor="weight"
            className="block text-sm font-medium text-gray-700"
          >
            Weight
          </label>
          <input
            id="weight"
            name="weight"
            type="number"
            step="0.01"
            min="0"
            required
            value={formData.weight}
            onChange={handleFormChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            placeholder="Enter weight"
          />
        </div>

        <div>
          <label
            htmlFor="units"
            className="block text-sm font-medium text-gray-700"
          >
            Units
          </label>
          <input
            id="units"
            name="units"
            type="number"
            min="0"
            value={formData.units}
            onChange={handleFormChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            placeholder="0"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Brand
          </label>
          <div className="flex flex-wrap gap-2">
            {Array.isArray(brands) &&
              brands.map((brand) => (
                <button
                  key={brand.id}
                  type="button"
                  className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ease-in-out ${
                    formData.device_brand_id === brand.id
                      ? "bg-indigo-100 text-indigo-700 border-indigo-500"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  } border`}
                  onClick={() => {
                    setFormData((prevData) => ({
                      ...prevData,
                      device_brand_id: brand.id,
                    }));
                  }}
                >
                  <span
                    className={`w-3 h-3 rounded-full mr-2 ${
                      formData.device_brand_id === brand.id
                        ? "bg-indigo-500"
                        : "bg-gray-300"
                    }`}
                  />
                  {brand.name}
                </button>
              ))}
            <a
              href="/admin/device-brands"
              className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors duration-200 ease-in-out"
            >
              <span className="w-3 h-3 rounded-full mr-2 bg-gray-300" />
              Other
            </a>
          </div>
        </div>

        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}

        <button
          type="button"
          onClick={handleFormSubmit}
          disabled={tonerMutation.isLoading}
          className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
            tonerMutation.isLoading
              ? "bg-blue-400 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700"
          } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200`}
        >
          {tonerMutation.isLoading ? "Saving..." : "Save Toner"}
        </button>
      </form>
    </div>
  );
}
